// Shared subscription feature data used by both DatabaseTool and SubscriptionSettings.
// Keep PLAN_DEFAULTS in sync with SubscriptionTierDefaults.cs in velaro-messaging.

export const PLAN_DEFAULTS: Record<string, Record<string, boolean>> = {
  basic: {
    EnableWeb: true, EnableSms: false, EnableEmail: false, EnableWhatsapp: false,
    EnableFacebook: false, EnableInstagram: false, EnableTikTok: false,
    EnableAI: true, EnableWorkflowRules: true, EnableAutomation: true,
    EnableMcp: false, EnableQuickStartWizards: true, EnableBotVoice: false,
    EnableOrchestration: false,
    EnableTierEscalation: false, EnablePriorityQueue: false, EnableVideoChat: false,
    EnableHubSpot: false, EnableHubSpotSkills: false, EnableDynamics: false,
    EnableNetsuite: false, EnableNetSuiteSkills: false, EnableSalesforce: false,
    EnablePipedriveSkills: false, EnableZohoSkills: false, EnableServiceNow: false,
    EnableBigCommerce: false, EnableShopify: false, EnableWooCommerce: false,
    EnableMagento: false, EnableSquare: false, EnableSquareRetail: false,
    EnableToastSkills: false, EnableQuickBooksSkills: false, EnableInChatPayments: false,
    EnableGuestySkills: false, EnableUplistingSkills: false, EnableCloudbedsSkills: false, EnableMindbodySkills: false,
    EnableAccela: false, EnableKlaviyo: false, EnableMondaySkills: false,
    EnableFormstack: false, EnableZapier: false, EnablePowerAutomate: false,
    EnableMake: false, EnableOneDriveAttachments: false, EnableTeamsNotifications: false,
    EnableSlackNotifications: false, EnableCalendarBooking: false, EnableConversationTraining: false,
    EnableBotEmailSkill: false, EnableAdvancedAiSkills: false, EnableDestructiveAiSkills: false,
    EnableMultipleDeployments: false, EnablePageWidgets: false,
    EnableEnterpriseSso: false, EnableAdvancedMfa: false, EnablePasskeys: false,
    EnableIpBlocking: false, EnableAgentIpRestriction: false, EnableSessionTimeout: false,
    EnableConcurrentSessionLimit: false, EnableDataRetention: false,
    EnableScheduledPurge: false, EnableTranscriptStorage: false, EnableMessageScrubbing: false,
  },
  pro: {
    EnableWeb: true, EnableSms: true, EnableEmail: true, EnableWhatsapp: true,
    EnableFacebook: true, EnableInstagram: false, EnableTikTok: false,
    EnableAI: true, EnableWorkflowRules: true, EnableAutomation: true,
    EnableMcp: true, EnableQuickStartWizards: true, EnableBotVoice: true,
    EnableOrchestration: false,
    EnableTierEscalation: false, EnablePriorityQueue: false, EnableVideoChat: false,
    EnableHubSpot: false, EnableHubSpotSkills: false, EnableDynamics: false,
    EnableNetsuite: false, EnableNetSuiteSkills: false, EnableSalesforce: false,
    EnablePipedriveSkills: false, EnableZohoSkills: false, EnableServiceNow: false,
    EnableBigCommerce: true, EnableShopify: true, EnableWooCommerce: false,
    EnableMagento: false, EnableSquare: false, EnableSquareRetail: false,
    EnableToastSkills: false, EnableQuickBooksSkills: false, EnableInChatPayments: false,
    EnableGuestySkills: false, EnableUplistingSkills: false, EnableCloudbedsSkills: false, EnableMindbodySkills: false,
    EnableAccela: false, EnableKlaviyo: false, EnableMondaySkills: false,
    EnableFormstack: false, EnableZapier: false, EnablePowerAutomate: false,
    EnableMake: false, EnableOneDriveAttachments: true, EnableTeamsNotifications: true,
    EnableSlackNotifications: true, EnableCalendarBooking: true, EnableConversationTraining: false,
    EnableBotEmailSkill: true, EnableAdvancedAiSkills: false, EnableDestructiveAiSkills: false,
    EnableMultipleDeployments: true, EnablePageWidgets: true,
    EnableEnterpriseSso: false, EnableAdvancedMfa: false, EnablePasskeys: false,
    EnableIpBlocking: false, EnableAgentIpRestriction: false, EnableSessionTimeout: false,
    EnableConcurrentSessionLimit: false, EnableDataRetention: false,
    EnableScheduledPurge: false, EnableTranscriptStorage: false, EnableMessageScrubbing: false,
  },
  enterprise: {
    EnableWeb: true, EnableSms: true, EnableEmail: true, EnableWhatsapp: true,
    EnableFacebook: true, EnableInstagram: true, EnableTikTok: true,
    EnableAI: true, EnableWorkflowRules: true, EnableAutomation: true,
    EnableMcp: true, EnableQuickStartWizards: true, EnableBotVoice: true,
    EnableOrchestration: true,
    EnableTierEscalation: true, EnablePriorityQueue: true, EnableVideoChat: false,
    EnableHubSpot: true, EnableHubSpotSkills: true, EnableDynamics: true,
    EnableNetsuite: true, EnableNetSuiteSkills: true, EnableSalesforce: true,
    EnablePipedriveSkills: true, EnableZohoSkills: true, EnableServiceNow: true,
    EnableBigCommerce: true, EnableShopify: true, EnableWooCommerce: true,
    EnableMagento: true, EnableSquare: true, EnableSquareRetail: true,
    EnableToastSkills: true, EnableQuickBooksSkills: true, EnableInChatPayments: false,
    EnableGuestySkills: true, EnableUplistingSkills: true, EnableCloudbedsSkills: true, EnableMindbodySkills: true,
    EnableAccela: true, EnableKlaviyo: true, EnableMondaySkills: true,
    EnableFormstack: true, EnableZapier: true, EnablePowerAutomate: true,
    EnableMake: true, EnableOneDriveAttachments: true, EnableTeamsNotifications: true,
    EnableSlackNotifications: true, EnableCalendarBooking: true, EnableConversationTraining: true,
    EnableBotEmailSkill: true, EnableAdvancedAiSkills: false, EnableDestructiveAiSkills: false,
    EnableMultipleDeployments: true, EnablePageWidgets: true,
    EnableEnterpriseSso: true, EnableAdvancedMfa: true, EnablePasskeys: true,
    EnableIpBlocking: true, EnableAgentIpRestriction: true, EnableSessionTimeout: true,
    EnableConcurrentSessionLimit: true, EnableDataRetention: true,
    EnableScheduledPurge: true, EnableTranscriptStorage: true, EnableMessageScrubbing: true,
  }
};

export interface FeatureItem {
  key: string;
  label: string;
}

export interface FeatureGroup {
  label: string;
  features: FeatureItem[];
}

export const FEATURE_GROUPS: FeatureGroup[] = [
  {
    label: "Inbound Channels",
    features: [
      { key: "EnableWeb",       label: "Web Messenger" },
      { key: "EnableSms",       label: "SMS" },
      { key: "EnableEmail",     label: "Email" },
      { key: "EnableWhatsapp",  label: "WhatsApp" },
      { key: "EnableFacebook",  label: "Facebook Messenger" },
      { key: "EnableInstagram", label: "Instagram DMs" },
      { key: "EnableTikTok",    label: "TikTok DMs" },
    ]
  },
  {
    label: "AI & Automation",
    features: [
      { key: "EnableAI",                  label: "AI Chatbots" },
      { key: "EnableWorkflowRules",       label: "Workflow Rules" },
      { key: "EnableAutomation",          label: "Automation" },
      { key: "EnableMcp",                 label: "MCP Servers" },
      { key: "EnableQuickStartWizards",   label: "Quick Setup Wizards" },
      { key: "EnableBotVoice",            label: "ElevenLabs Bot Voice (TTS)" },
      { key: "EnableOrchestration",       label: "AI Orchestration (multi-bot)" },
      { key: "EnableBotEmailSkill",       label: "Bot Email Skill" },
      { key: "EnableAdvancedAiSkills",    label: "Advanced AI Skills (Write tier)" },
      { key: "EnableDestructiveAiSkills", label: "Destructive AI Skills (cancel / refund)" },
    ]
  },
  {
    label: "Routing & Queuing",
    features: [
      { key: "EnableTierEscalation", label: "Tier Escalation Routing" },
      { key: "EnablePriorityQueue",  label: "Priority / VIP Queue" },
      { key: "EnableVideoChat",      label: "Video Chat" },
    ]
  },
  {
    label: "CRM Integrations",
    features: [
      { key: "EnableHubSpot",          label: "HubSpot CRM" },
      { key: "EnableHubSpotSkills",    label: "HubSpot AI Skills" },
      { key: "EnableDynamics",         label: "Microsoft Dynamics 365" },
      { key: "EnableNetsuite",         label: "NetSuite" },
      { key: "EnableNetSuiteSkills",   label: "NetSuite AI Skills" },
      { key: "EnableSalesforce",       label: "Salesforce" },
      { key: "EnablePipedriveSkills",  label: "Pipedrive AI Skills" },
      { key: "EnableZohoSkills",       label: "Zoho CRM AI Skills" },
      { key: "EnableServiceNow",       label: "ServiceNow" },
    ]
  },
  {
    label: "Commerce / POS",
    features: [
      { key: "EnableBigCommerce",      label: "BigCommerce" },
      { key: "EnableShopify",          label: "Shopify" },
      { key: "EnableWooCommerce",      label: "WooCommerce" },
      { key: "EnableMagento",          label: "Magento / Adobe Commerce" },
      { key: "EnableSquare",           label: "Square (Appointments / T1)" },
      { key: "EnableSquareRetail",     label: "Square Retail (Catalog / T2)" },
      { key: "EnableToastSkills",      label: "Toast POS AI Skills" },
      { key: "EnableQuickBooksSkills", label: "QuickBooks AI Skills" },
      { key: "EnableInChatPayments",   label: "In-Chat Payments (Stripe)" },
    ]
  },
  {
    label: "Hospitality",
    features: [
      { key: "EnableGuestySkills",     label: "Guesty (Vacation Rental)" },
      { key: "EnableUplistingSkills",  label: "Uplisting (Short-Term Rental)" },
      { key: "EnableCloudbedsSkills",  label: "Cloudbeds (Hotel PMS)" },
      { key: "EnableMindbodySkills",   label: "Mindbody (Fitness / Wellness)" },
    ]
  },
  {
    label: "Productivity & Automation",
    features: [
      { key: "EnableAccela",              label: "Accela Civic (Government)" },
      { key: "EnableKlaviyo",             label: "Klaviyo" },
      { key: "EnableMondaySkills",        label: "Monday.com AI Skills" },
      { key: "EnableFormstack",           label: "Formstack" },
      { key: "EnableZapier",              label: "Zapier" },
      { key: "EnablePowerAutomate",       label: "Power Automate" },
      { key: "EnableMake",                label: "Make.com" },
      { key: "EnableOneDriveAttachments", label: "OneDrive Attachments" },
      { key: "EnableTeamsNotifications",  label: "Teams Notifications" },
      { key: "EnableSlackNotifications",  label: "Slack Notifications" },
      { key: "EnableCalendarBooking",     label: "Calendar Booking (Outlook / Calendly)" },
      { key: "EnableConversationTraining",label: "Conversation Training" },
    ]
  },
  {
    label: "Platform",
    features: [
      { key: "EnableMultipleDeployments", label: "Multiple Deployments" },
      { key: "EnablePageWidgets",         label: "Page Widgets" },
    ]
  },
  {
    label: "Security & Compliance",
    features: [
      { key: "EnableEnterpriseSso",           label: "Enterprise SSO" },
      { key: "EnableAdvancedMfa",             label: "Advanced MFA" },
      { key: "EnablePasskeys",                label: "Passkeys / FIDO2" },
      { key: "EnableIpBlocking",              label: "Visitor IP Blocking" },
      { key: "EnableAgentIpRestriction",      label: "Agent Login IP Restriction" },
      { key: "EnableSessionTimeout",          label: "Session Timeout" },
      { key: "EnableConcurrentSessionLimit",  label: "Concurrent Session Limit" },
      { key: "EnableDataRetention",           label: "Data Retention (GDPR Purge)" },
      { key: "EnableScheduledPurge",          label: "Scheduled Auto-Purge" },
      { key: "EnableTranscriptStorage",       label: "BYOS Transcript Storage" },
      { key: "EnableMessageScrubbing",        label: "Message Scrubbing (PII)" },
    ]
  },
];
